import React, { useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill, glassTile, glassPanel } from "../../constants/gradCard";
import { formatPropAddr } from "../../utils/propertyAddr";

function SpJobsList({ sp }) {
  const { t, h, go, flash, setSelMaintId } = useContext(Ctx);
  if (sp.loading) return <div style={{ paddingTop: 40, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 13 }}>{t.syncingDb}</div></div>;
  const spJobs = sp.data?.jobs || []; const spTens = sp.data?.tens || []; const spProps = sp.data?.props || [];

  // Show active jobs + completed-but-unconfirmed (SP may still need to enter charges)
  const activeJobs = spJobs.filter(j => !j.confirmed_by_tenant);

  const updateStatus = async (job, newStatus) => {
    const updates = { id: job.id, sp_status: newStatus };
    if (newStatus === 'completed') { updates.completion_token = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`; }
    await api.serviceUpdateJob(updates);
    flash(newStatus === 'accepted' ? t.jobAccepted : newStatus === 'in_progress' ? t.workStarted : t.jobCompleted);
    sp.reload();
  };

  return <div style={{ paddingTop: 16 }}>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{t.myJobs}</h2>
    {activeJobs.length === 0 && <div style={{ color: h.sc, textAlign: "center", padding: 40, fontSize: 13 }}>{t.noActiveJobs}</div>}
    {[...activeJobs].sort((a, b) => {
      // Status priority: brand-new (pending) → accepted → in_progress → completed-but-unconfirmed
      const rank = { pending: 0, accepted: 1, in_progress: 2, completed: 3 };
      const ra = rank[a.sp_status] ?? 9;
      const rb = rank[b.sp_status] ?? 9;
      if (ra !== rb) return ra - rb;
      // Within same status, newest first (id format "m<timestamp>")
      return (b.id || "").localeCompare(a.id || "");
    }).map(j => { const ten = spTens.find(x => x.id === j.tid); const prop = ten ? spProps.find(x => x.id === ten.pid) : (spProps.length === 1 ? spProps[0] : null);
      const mapsUrl = prop?.latitude && prop?.longitude
        ? `https://www.google.com/maps/dir/?api=1&destination=${prop.latitude},${prop.longitude}`
        : null;
      // Smart state by job stage: pending = amber (needs action),
      // accepted = indigo, in-progress = violet, completed = green.
      const tone = j.sp_status === 'pending' ? "amber" : j.sp_status === 'in_progress' ? "violet" : j.sp_status === 'completed' ? "green" : "indigo";
      return <div key={j.id}
        onMouseOver={(e) => gradLift(e, tone, true)}
        onMouseOut={(e) => gradLift(e, tone, false)}
        style={{ ...crd(h), ...gradCardStyle(tone) }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <div><div style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>{j.desc}</div><div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2 }}>{ten?.name || (t.generalJob || "General Job")} · {prop ? formatPropAddr(prop) : (t.noProperty || "No property")}</div><div style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>{j.dt}</div></div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
            <span style={glassPill}>{j.urg === "high" ? t.high : j.urg === "med" ? t.med : t.low}</span>
            <span style={glassPill}>{j.sp_status === 'pending' ? t.pending : j.sp_status === 'accepted' ? (t.accepted || "Accepted") : j.sp_status === 'in_progress' ? (t.inProgress || "In Progress") : j.sp_status}</span>
          </div>
        </div>
        {/* Show SP charges if entered */}
        {(j.sp_pay_amt || 0) > 0 && <div style={{ ...glassPanel(h), display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: h.tx }}>💰 {t.myCharges || "My Charges"}: <span style={{ fontWeight: 700 }}>₹{j.sp_pay_amt.toLocaleString()}</span></span>
          <span style={bdg(j.sp_pay_st === 'paid' ? `${h.gn}20` : `${h.wn}20`, j.sp_pay_st === 'paid' ? h.gn : h.wn)}>{j.sp_pay_st === 'paid' ? "✅ Paid" : "⏳ Pending"}</span>
        </div>}
        {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "8px 0", borderRadius: 10, background: "rgba(255,255,255,0.92)", border: "1px solid rgba(255,255,255,0.6)", color: "#0f9d6b", fontSize: 12, fontWeight: 600, textDecoration: "none", marginBottom: 8 }}>
          🗺️ Get Directions
        </a>}
        {ten?.ph && <div style={{ marginBottom: 8 }}><a href={`tel:${ten.ph}`} style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>{"📞 " + ten.ph}</a></div>}
        <div style={{ display: "flex", gap: 8 }}>
          {j.sp_status === 'pending' && <button onClick={() => updateStatus(j, 'accepted')} style={glassTile(h.ac)}>{t.acceptJob}</button>}
          {j.sp_status === 'accepted' && <button onClick={() => updateStatus(j, 'in_progress')} style={glassTile(h.pp || h.ac)}>{t.startWork}</button>}
          {(j.sp_status === 'in_progress' || j.sp_status === 'completed') && <>
            <button onClick={() => { setSelMaintId(j.id); go("spJobDetail"); }} style={glassTile(h.ac)}>{(j.sp_pay_amt || 0) > 0 ? (t.editCharges || "Edit Charges") : t.jobDetails}</button>
            {j.sp_status === 'in_progress' && <button onClick={() => updateStatus(j, 'completed')} style={glassTile(h.gn)}>{t.markComplete}</button>}
          </>}
        </div>
      </div>; })}
  </div>;
}

export default SpJobsList;
